import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PhoneInput } from "@/components/ui/phone-input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { useBuyerLists } from "@/hooks/useBuyerLists";
import { sellerCreatedBuyerService } from "@/services/sellerCreatedBuyerService";
import { buyerListService } from "@/services/buyerListService";
import { ChevronLeft, ChevronRight, Lock } from "lucide-react";
import { PURCHASING_TYPES, CLOSE_TIMES } from "@/constants/buyerFormOptions";
import { usStates } from "@/utils/states";
import { ScrollArea } from "@/components/ui/scroll-area";
import { z } from "zod";

interface CreateManualBuyerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onBuyerCreated?: () => void;
}

const manualBuyerSchema = z.object({
  fullName: z.string().min(1, "Full name is required").max(100),
  companyName: z.string().max(100).optional(),
  email: z.string().email("Invalid email address").max(255).optional().or(z.literal('')),
  phoneNumber: z.string().min(1, "Phone number is required"),
  dealsAnnually: z.number().min(0, "Must be 0 or greater"),
  preferredMarkets: z.array(z.string()).min(1, "Select at least one market"),
  purchasingType: z.array(z.string()).min(1, "Select at least one purchasing type"),
  averageCloseTime: z.string().optional(),
  notes: z.string().max(500).optional(),
  listOption: z.enum(['existing', 'new']),
  existingListId: z.string().optional(),
  newListName: z.string().optional(),
  newListDescription: z.string().max(500).optional(),
}).refine(
  (data) => {
    if (data.listOption === 'existing') {
      return !!data.existingListId;
    } else {
      return !!data.newListName && data.newListName.length > 0;
    }
  },
  {
    message: "Please select an existing list or provide a name for a new list",
    path: ["listOption"],
  }
);

export const CreateManualBuyerDialog = ({ open, onOpenChange, onBuyerCreated }: CreateManualBuyerDialogProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { lists } = useBuyerLists();
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  const [formData, setFormData] = useState({
    fullName: '',
    companyName: '',
    email: '',
    phoneNumber: '',
    dealsAnnually: 0,
    preferredMarkets: [] as string[],
    purchasingType: [] as string[],
    averageCloseTime: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    listOption: 'existing' as 'existing' | 'new',
    existingListId: '',
    newListName: '',
    newListDescription: '',
    isPrivate: true, // Always true for private buyers
    notes: ''
  });

  const handleReset = () => {
    setFormData({
      fullName: '',
      companyName: '',
      email: '',
      phoneNumber: '',
      dealsAnnually: 0,
      preferredMarkets: [],
      purchasingType: [],
      averageCloseTime: '',
      address: '',
      city: '',
      state: '',
      zipCode: '',
      listOption: 'existing',
      existingListId: '',
      newListName: '',
      newListDescription: '',
      isPrivate: true, // Always true for private buyers
      notes: ''
    });
    setStep(1);
    setErrors({});
  };

  const validateStep = (stepNumber: number): boolean => {
    const newErrors: Record<string, string> = {};

    if (stepNumber === 1) {
      if (!formData.fullName.trim()) newErrors.fullName = "Full name is required";
      if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = "Invalid email address";
      }
      if (!formData.phoneNumber.trim()) {
        newErrors.phoneNumber = "Phone number is required";
      }
      if (formData.dealsAnnually < 0) newErrors.dealsAnnually = "Must be 0 or greater";
    } else if (stepNumber === 2) {
      if (formData.purchasingType.length === 0) {
        newErrors.purchasingType = "Select at least one purchasing type";
      }
      if (formData.preferredMarkets.length === 0) {
        newErrors.preferredMarkets = "Select at least one market";
      }
    } else if (stepNumber === 3) {
      if (formData.listOption === 'existing' && !formData.existingListId) {
        newErrors.listOption = "Please select a list";
      }
      if (formData.listOption === 'new' && !formData.newListName.trim()) {
        newErrors.newListName = "List name is required";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(step)) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    setStep(step - 1);
    setErrors({});
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) return;
    if (!user?.id) return;

    setIsSubmitting(true);
    
    try {
      const validated = manualBuyerSchema.parse(formData);
      
      const { data: buyer, error: buyerError } = await sellerCreatedBuyerService.createBuyer({
        sellerId: user.id,
        fullName: validated.fullName,
        companyName: validated.companyName,
        email: validated.email,
        phoneNumber: validated.phoneNumber,
        dealsAnnually: validated.dealsAnnually,
        preferredMarkets: validated.preferredMarkets,
        purchasingType: validated.purchasingType,
        averageCloseTime: validated.averageCloseTime,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        zipCode: formData.zipCode,
        isPrivate: true, // Always private for seller-created buyers
        notes: validated.notes
      });
      
      if (buyerError || !buyer) {
        toast({
          title: "Error",
          description: buyerError || "Failed to create buyer",
          variant: "destructive"
        });
        return;
      }
      
      let listId = formData.existingListId;
      
      if (formData.listOption === 'new') {
        const { data: newList, error: listError } = await buyerListService.createList(
          user.id,
          formData.newListName,
          formData.newListDescription
        );
        
        if (listError || !newList) {
          toast({
            title: "Error",
            description: "Buyer created but failed to create list",
            variant: "destructive"
          });
          return;
        }
        
        listId = newList.id;
      }
      
      const { success, error: addError } = await sellerCreatedBuyerService.addBuyerToList(
        listId,
        buyer.id,
        formData.notes
      );
      
      if (!success) {
        toast({
          title: "Warning",
          description: addError || "Buyer created but failed to add to list",
          variant: "destructive"
        });
        return;
      }
      
      toast({
        title: "Success!",
        description: "Private buyer created and added to list",
      });
      
      handleReset();
      onBuyerCreated?.();
      onOpenChange(false);
      
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: Record<string, string> = {};
        error.errors.forEach(err => {
          if (err.path) {
            fieldErrors[err.path[0] as string] = err.message;
          }
        });
        setErrors(fieldErrors);
      } else {
        toast({
          title: "Error",
          description: "An unexpected error occurred",
          variant: "destructive"
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleMarket = (market: string) => {
    setFormData(prev => ({
      ...prev,
      preferredMarkets: prev.preferredMarkets.includes(market)
        ? prev.preferredMarkets.filter(m => m !== market)
        : [...prev.preferredMarkets, market]
    }));
  };

  const togglePurchasingType = (type: string) => {
    setFormData(prev => ({
      ...prev,
      purchasingType: prev.purchasingType.includes(type)
        ? prev.purchasingType.filter(t => t !== type)
        : [...prev.purchasingType, type]
    }));
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      if (!isOpen) handleReset();
      onOpenChange(isOpen);
    }}>
      <DialogContent className="max-w-2xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>
            Add Private Buyer - {step === 1 ? 'Personal Info' : step === 2 ? 'Preferences' : 'Add to List'} [{step}/3]
          </DialogTitle>
          <DialogDescription>
            {step === 1 && "Enter the buyer's personal information"}
            {step === 2 && "Select their investment preferences"}
            {step === 3 && "Add to a buyer list"}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] pr-4">
          {/* Step 1: Personal Information */}
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="fullName">Full Name *</Label>
                <Input
                  id="fullName"
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  placeholder="John Smith"
                />
                {errors.fullName && <p className="text-sm text-destructive mt-1">{errors.fullName}</p>}
              </div>

              <div>
                <Label htmlFor="companyName">Company Name</Label>
                <Input
                  id="companyName"
                  value={formData.companyName}
                  onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                  placeholder="ABC Investments"
                />
              </div>

              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@abc.com"
                />
                {errors.email && <p className="text-sm text-destructive mt-1">{errors.email}</p>}
              </div>

              <div>
                <Label htmlFor="phoneNumber">Phone Number *</Label>
                <PhoneInput
                  id="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={(value) => setFormData({ ...formData, phoneNumber: value })}
                  showValidation={true}
                />
                {errors.phoneNumber && <p className="text-sm text-destructive mt-1">{errors.phoneNumber}</p>}
              </div>

              <div>
                <Label htmlFor="dealsAnnually">Number of Deals Annually *</Label>
                <Input
                  id="dealsAnnually"
                  type="number"
                  min="0"
                  value={formData.dealsAnnually}
                  onChange={(e) => setFormData({ ...formData, dealsAnnually: parseInt(e.target.value) || 0 })}
                />
                {errors.dealsAnnually && <p className="text-sm text-destructive mt-1">{errors.dealsAnnually}</p>}
              </div>
            </div>
          )}

          {/* Step 2: Investment Preferences */}
          {step === 2 && (
            <div className="space-y-4">
              <div>
                <Label>Purchasing Types (Select at least one) *</Label>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  {PURCHASING_TYPES.map((type) => (
                    <div key={type} className="flex items-center space-x-2">
                      <Checkbox
                        id={`type-${type}`}
                        checked={formData.purchasingType.includes(type)}
                        onCheckedChange={() => togglePurchasingType(type)}
                      />
                      <label htmlFor={`type-${type}`} className="text-sm cursor-pointer">
                        {type}
                      </label>
                    </div>
                  ))}
                </div>
                {errors.purchasingType && <p className="text-sm text-destructive mt-1">{errors.purchasingType}</p>}
              </div>

              <div>
                <Label htmlFor="averageCloseTime">Average Close Time</Label>
                <Select
                  value={formData.averageCloseTime}
                  onValueChange={(value) => setFormData({ ...formData, averageCloseTime: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select close time" />
                  </SelectTrigger>
                  <SelectContent>
                    {CLOSE_TIMES.map((option) => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Preferred Markets (Select at least one) *</Label>
                <ScrollArea className="h-48 border rounded-md p-3 mt-2">
                  <div className="grid grid-cols-2 gap-3">
                    {usStates.map((state) => (
                      <div key={state.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`market-${state.id}`}
                          checked={formData.preferredMarkets.includes(state.name)}
                          onCheckedChange={() => toggleMarket(state.name)}
                        />
                        <label htmlFor={`market-${state.id}`} className="text-sm cursor-pointer">
                          {state.name}
                        </label>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
                {errors.preferredMarkets && <p className="text-sm text-destructive mt-1">{errors.preferredMarkets}</p>}
              </div>
            </div>
          )}

          {/* Step 3: List Assignment */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="border rounded-lg p-4 bg-muted/30">
                <div className="flex items-center gap-2 mb-2">
                  <Lock className="h-4 w-4 text-muted-foreground" />
                  <p className="text-sm font-medium">Private Buyer</p>
                </div>
                <p className="text-xs text-muted-foreground">
                  Private buyers are only visible to you and won't appear in search results
                </p>
              </div>

              <div>
                <Label>Add to List</Label>
                <RadioGroup
                  value={formData.listOption}
                  onValueChange={(value: 'existing' | 'new') => setFormData({ ...formData, listOption: value })}
                  className="mt-2"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="existing" id="existing" />
                    <Label htmlFor="existing" className="font-normal cursor-pointer">Add to existing list</Label>
                  </div>
                  {formData.listOption === 'existing' && (
                    <div className="ml-6 mt-2">
                      <Select
                        value={formData.existingListId}
                        onValueChange={(value) => setFormData({ ...formData, existingListId: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select a list" />
                        </SelectTrigger>
                        <SelectContent>
                          {lists.map((list) => (
                            <SelectItem key={list.id} value={list.id}>
                              {list.list_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div className="flex items-center space-x-2 mt-3">
                    <RadioGroupItem value="new" id="new" />
                    <Label htmlFor="new" className="font-normal cursor-pointer">Create new list</Label>
                  </div>
                  {formData.listOption === 'new' && (
                    <div className="ml-6 mt-2 space-y-3">
                      <div>
                        <Label htmlFor="newListName">List Name *</Label>
                        <Input
                          id="newListName"
                          value={formData.newListName}
                          onChange={(e) => setFormData({ ...formData, newListName: e.target.value })}
                          placeholder="Atlanta Investors"
                        />
                        {errors.newListName && <p className="text-sm text-destructive mt-1">{errors.newListName}</p>}
                      </div>
                      <div>
                        <Label htmlFor="newListDescription">Description</Label>
                        <Textarea
                          id="newListDescription"
                          value={formData.newListDescription}
                          onChange={(e) => setFormData({ ...formData, newListDescription: e.target.value })}
                          placeholder="Investors interested in..."
                          rows={2}
                        />
                      </div>
                    </div>
                  )}
                </RadioGroup>
                {errors.listOption && <p className="text-sm text-destructive mt-1">{errors.listOption}</p>}
              </div>

              <div>
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Met at conference, very active..."
                  rows={3}
                  maxLength={500}
                />
                <p className="text-xs text-muted-foreground mt-1">{formData.notes.length}/500</p>
              </div>
            </div>
          )}
        </ScrollArea>

        <div className="flex justify-between gap-3 mt-4">
          {step > 1 ? (
            <Button variant="outline" onClick={handleBack}>
              <ChevronLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          ) : (
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
          )}

          {step < 3 ? (
            <Button onClick={handleNext}>
              Next
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? "Creating..." : "Add Private Buyer"}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
